import { classNames } from '@/utils';

import type {
    NestedOptionType,
    RenderPropsType
} from '@/components/composite/CaptionComponent/Components/CaptionFooter/CaptionAi/types/types';

import Dropdown from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import { AIDropdownButton } from '@/components/composite/CaptionComponent/Components/CaptionFooter/CaptionAi/Components/AiDropdownButton';
import { optionStyle } from '@/components/composite/CaptionComponent/Components/CaptionFooter/CaptionAi/config/optionStyle';

type AiDropdownOptionType = {
    label: string;
    value?: string;
    icon?: string;
    tag?: string;
    disabled?: boolean;
} & ({ callback: (...args: unknown[]) => void } | { submenu: NestedOptionType[] });

export type AiDropdownProps = {
    readonly position?: React.ComponentProps<typeof Dropdown>['position'];
    readonly dropdownOptions: { tag: string; options: AiDropdownOptionType[] }[];
    readonly disabled: boolean;
};

export function AiDropdown({ dropdownOptions, position = 'top', disabled }: AiDropdownProps) {
    return (
        <Dropdown
            id="ai-dropdown"
            classNameWrapper="w-max"
            position={position}
            size="full"
            hasBorder
            className="divide-y divide-icoGray-300 dark:divide-icoDarkMode-beton"
            button={<AIDropdownButton isDisabled={disabled} />}>
            {dropdownOptions.map((menuOptions, menuIndex) => {
                return (
                    <div
                        className="flex w-full flex-col space-y-px p-0.5"
                        key={`menu-${menuOptions.tag || menuIndex}`}>
                        {menuOptions?.tag ? (
                            <span className="mb-2 ml-2 mt-3 w-min rounded-px bg-icoBlue-100 p-0.75 text-8 uppercase tracking-widest text-icoBlue dark:bg-icoBlue/20">
                                {menuOptions?.tag}
                            </span>
                        ) : null}

                        {menuOptions.options.map((option, optionIndex) => {
                            if ('submenu' in option && option.submenu) {
                                return (
                                    <span
                                        key={`submenu-${option.label || optionIndex}`}
                                        className="group w-full">
                                        <Tooltip
                                            variant="wrapper"
                                            arrow={false}
                                            interactive
                                            disabled={option.disabled}
                                            position="right-start"
                                            content={
                                                <div className="scrollbar max-h-40 min-w-[120px]">
                                                    {option.submenu.map(
                                                        (nestedOption, nestedIndex) => (
                                                            <button
                                                                key={`nested-${
                                                                    nestedOption.label ||
                                                                    nestedIndex
                                                                }`}
                                                                className="flex w-full cursor-pointer gap-2 rounded-m p-2 text-left transition-colors hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/20"
                                                                onClick={nestedOption.callback}
                                                                disabled={false}
                                                                data-testid={`item-${nestedOption.label}`}>
                                                                <span className="inline-flex items-center">
                                                                    {nestedOption.label}
                                                                </span>
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            }>
                                            <span className="w-full">
                                                <Dropdown.Item
                                                    key={`item-${option.label || optionIndex}`}
                                                    disabled={option.disabled}
                                                    data-testid={`item-${option.label}`}
                                                    className="flex">
                                                    {({ active, disabled }: RenderPropsType) => (
                                                        <span
                                                            className={classNames(
                                                                'flex w-full items-center justify-between gap-2 rounded-m p-2 text-left group-hover:bg-icoBlue-100 group-hover:text-icoBlue group-hover:dark:bg-icoBlue/20',
                                                                {
                                                                    [optionStyle.active]: active,
                                                                    [`${optionStyle.disabled} group-hover:!bg-icoGray-300 group-hover:!text-icoGray-400 group-hover:dark:!bg-white/5 group-hover:dark:!text-icoDarkMode-wolf`]:
                                                                        disabled
                                                                }
                                                            )}>
                                                            <span className="flex items-center gap-2">
                                                                <Icon icon={option.icon} />
                                                                <span>{option.label}</span>
                                                            </span>
                                                            <Icon icon="chevron-right" />
                                                        </span>
                                                    )}
                                                </Dropdown.Item>
                                            </span>
                                        </Tooltip>
                                    </span>
                                );
                            }

                            return (
                                <Dropdown.Item
                                    disabled={option.disabled}
                                    key={`option-${option.label || optionIndex}`}
                                    data-testid={`item-${option.label}`}
                                    className="flex"
                                    onClick={'callback' in option && option.callback}>
                                    {({ active, disabled }: RenderPropsType) => (
                                        <span
                                            className={classNames(
                                                'flex w-full cursor-pointer gap-2 rounded-m p-2 text-left',
                                                {
                                                    [optionStyle.active]: active,
                                                    [optionStyle.disabled]: disabled
                                                }
                                            )}>
                                            <Icon icon={option.icon} />
                                            <span>{option.label}</span>
                                        </span>
                                    )}
                                </Dropdown.Item>
                            );
                        })}
                    </div>
                );
            })}
        </Dropdown>
    );
}
